import React from "react";
import { Listing } from "src/types/listing";
import {
  Bath,
  Bed,
  Grid,
  CalendarClock,
} from "@landvoice/icons";
import { Scaling, House } from "lucide-react";
import { Divider, InfoIcon } from "../../common/PropertySummary";

interface PropertyStatsProps {
  listing: Listing;
  variant?: "detailed" | "compact";
}

const PropertyStats = ({
  listing,
  variant = "detailed",
}: PropertyStatsProps) => {
  const {
    beds,
    bath,
    sqrFeet,
    lotSize,
    formattedLotSize,
    yearBuilt,
    type,
    price = 0,
    formattedPrice,
    dom,
  } = listing;

  const isCompact = variant === "compact";

  // Style classes based on variant
  const containerClass = isCompact
    ? "stats-container flex gap-3 flex-wrap"
    : "stats-container flex flex-row gap-2 pt-3 flex-wrap items-center";

  const textClass = isCompact
    ? "text-[8px] leading-[10px]"
    : "text-[14px] leading-5";
  const iconSize = isCompact
    ? "max-h-[10px] max-w-[10px]"
    : "max-h-[16px] max-w-[16px]";
  const iconGap = "4px";

  return (
    <div className={containerClass}>
      {/* Price */}
      {!!price && (
        <>
          <p className={`${textClass} font-bold text-gray-primary`}>
            {price ? formattedPrice : "--"}
          </p>
          <Divider />
        </>
      )}

      {/* Days on Market */}
      {dom && (
        <>
          <p className={`${textClass} font-bold text-gray-primary`}>
            {dom} Days on Market
          </p>
          <Divider />
        </>
      )}

      {/* Beds */}
      <InfoIcon
        icon={<Bed className={iconSize} />}
        text={`${beds ? beds : "--"} Bed`}
        textSize={isCompact ? "8px" : "14px"}
        gap={iconGap}
      />
      <Divider />

      {/* Bath */}
      <InfoIcon
        icon={<Bath className={iconSize} />}
        text={`${bath ? bath : "--"} Bath`}
        textSize={isCompact ? "8px" : "14px"}
        gap={iconGap}
      />
      <Divider />

      {/* Square Feet */}
      <InfoIcon
        icon={<Grid className={iconSize} />}
        text={`${
          sqrFeet
            ? sqrFeet.toLocaleString("en-US", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })
            : "--"
        }  Sq Ft`}
        textSize={isCompact ? "8px" : "14px"}
        gap={iconGap}
      />
      <Divider />

      {/* Lot Size */}
      <InfoIcon
        icon={<Scaling className={iconSize} />}
        text={` LtSz ${lotSize ? formattedLotSize : "--"}`}
        textSize={isCompact ? "8px" : "14px"}
        gap={iconGap}
      />
      <Divider />

      {/* Year Built */}
      <InfoIcon
        icon={<CalendarClock className={iconSize} />}
        text={`Built ${yearBuilt || "--"}`}
        textSize={isCompact ? "8px" : "14px"}
        gap={iconGap}
      />
      <Divider />

      {/* Property Type */}
      <InfoIcon
        icon={<House className={iconSize} />}
        text={`${type ? type : "--"} `}
        textSize={isCompact ? "8px" : "14px"}
        gap={iconGap}
      />
    </div>
  );
};

export default PropertyStats;
